import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UploadFile } from '@mui/icons-material'
import { Button, FormHelperText, FormLabel, Stack, Typography } from '@mui/material'

// Not `application/vnd.ms-excel`: Windows browsers give it to a .csv, but also to an .xls, so such a file needs the extension.
const csvTypes = ['text/csv', 'application/csv', 'text/comma-separated-values', 'text/x-csv']

export const isCsvFile = (file: File) => file.name.toLowerCase().endsWith('.csv') || csvTypes.includes(file.type.toLowerCase())

type ImportFileFieldProps = {
  label: string
  value: File | null
  onChange: (file: File | null) => void
}

export const ImportFileField: FC<ImportFileFieldProps> = ({ label, value, onChange }) => {
  const { t } = useTranslation()
  const [rejectedName, setRejectedName] = useState<string>()

  const select = (file: File | null) => {
    if (file && !isCsvFile(file)) {
      setRejectedName(file.name)
      onChange(null)
      return
    }
    setRejectedName(undefined)
    onChange(file)
  }

  return (
    <Stack spacing={0.5}>
      <FormLabel error={!!rejectedName}>{label}</FormLabel>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button component="label" variant="outlined" startIcon={<UploadFile />}>
          {t('internal-analysts:import_tool_choose_file')}
          <input
            hidden
            type="file"
            accept=".csv,text/csv"
            aria-label={label}
            onChange={(event) => {
              select(event.target.files?.[0] ?? null)
              event.target.value = ''
            }}
          />
        </Button>
        <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'} noWrap>
          {value?.name ?? t('internal-analysts:import_tool_no_file')}
        </Typography>
      </Stack>
      {rejectedName && <FormHelperText error>{t('internal-analysts:import_tool_file_not_csv', { name: rejectedName })}</FormHelperText>}
    </Stack>
  )
}
